import { ForwardedRef, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, ButtonProps } from '@givebutter/primitives/button';
import * as CreateCampaignDialog from 'components/create-campaign-dialog';
import { useRecommendedActionContext } from 'components/recommended-actions/RecommendedActionContext';
import * as RecommendedActionsPlusDialog from 'components/recommended-actions/dialogs/RecommendedActionsPlusDialog';
import * as RecommendedActionsVerifyNonProfitDialog from 'components/recommended-actions/dialogs/RecommendedActionsVerifyNonProfitDialog';
import { posthogEvents } from 'constants/posthog-events';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { usePosthog } from 'customHooks/usePosthog';

interface RecommendedActionButtonStrategyProps extends Omit<ButtonProps, 'variant'> {}
const RecommendedActionButtonStrategy = (props: RecommendedActionButtonStrategyProps) => {
    const { action } = useRecommendedActionContext();
    const { actionKey, getStartedLink } = action;

    const { isAccountVerified, type, hasGivebutterPlusAccess } = useCurrentAccount();

    const isNonProfit = type === 'nonprofit';
    const isNonProfitVerified = isAccountVerified;
    const requiresEngage =
        actionKey === 'createFirstMarketingEmail' || actionKey === 'sendFirstMarketingEmail';
    const requiresPlus = actionKey === 'createSMS';

    if (actionKey === 'createEventCampaign') {
        return (
            <CreateCampaignDialog.Root
                triggerLocation={CreateCampaignDialog.TriggerLocation.RecommendedActions}
                defaultType="event"
            >
                <CreateCampaignDialog.Trigger asChild>
                    <ButtonWithPosthogClickEvent {...props} />
                </CreateCampaignDialog.Trigger>
            </CreateCampaignDialog.Root>
        );
    } else if (isNonProfit && !isNonProfitVerified && requiresEngage) {
        return (
            <RecommendedActionsVerifyNonProfitDialog.Root>
                <RecommendedActionsVerifyNonProfitDialog.Trigger asChild>
                    <ButtonWithPosthogClickEvent {...props} />
                </RecommendedActionsVerifyNonProfitDialog.Trigger>
            </RecommendedActionsVerifyNonProfitDialog.Root>
        );
    } else if ((requiresPlus && !hasGivebutterPlusAccess) || actionKey === 'startPlusTrial') {
        return (
            <RecommendedActionsPlusDialog.Root>
                <RecommendedActionsPlusDialog.Trigger asChild>
                    <ButtonWithPosthogClickEvent {...props} />
                </RecommendedActionsPlusDialog.Trigger>
            </RecommendedActionsPlusDialog.Root>
        );
    } else if (actionKey === 'downloadMobileApp') {
        return (
            <a href={getStartedLink} target="_blank" rel="noreferrer" tabIndex={-1}>
                <ButtonWithPosthogClickEvent {...props} />
            </a>
        );
    } else {
        return (
            <Link to={getStartedLink} tabIndex={-1}>
                <ButtonWithPosthogClickEvent {...props} />
            </Link>
        );
    }
};

interface ButtonWithPosthogClickEventProps extends Omit<ButtonProps, 'variant'> {}
const ButtonWithPosthogClickEvent = forwardRef(
    (
        { onClick, ...rest }: ButtonWithPosthogClickEventProps,
        ref: ForwardedRef<HTMLButtonElement>,
    ) => {
        const { pushEvent } = usePosthog();
        const { action } = useRecommendedActionContext();
        return (
            <Button
                ref={ref}
                variant="none"
                onClick={event => {
                    pushEvent(posthogEvents.DASHBOARD_RECOMMENDED_ACTION_SELECTED, {
                        action: action.actionKey,
                    });
                    onClick?.(event);
                }}
                {...rest}
            />
        );
    },
);

export { RecommendedActionButtonStrategy };
